import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../services/api';
import { msgErro, toastOk, toastErro, confirmar } from '../services/ui';
import { useAuth } from '../contexts/AuthContext';
import { fmtQtd, un, fmtDataHora, precoTxt, moeda, paraNumero, nomeBonito, digitarBanco, Situacao } from './cotacoesUtil';
import './Pedidos.css';
import './Cotacoes.css';

// O que está errado no preço digitado (null = ok). Em branco é "não cotar", não é erro.
const erroPreco = (s) => {
  const n = paraNumero(s);
  if (Number.isNaN(n)) return 'Use só números, ex.: 12,50';
  if (n != null && n <= 0) return 'Para não cotar, deixe em branco';
  return null;
};
// Preço que mudou 10× (pra mais ou pra menos) desde o último envio: provável vírgula no lugar errado — só avisa.
const desconfia = (novo, enviado) => novo > 0 && enviado > 0 && (novo / enviado >= 10 || enviado / novo >= 10);
const lerLS = (k) => { try { return JSON.parse(localStorage.getItem(k) || 'null'); } catch { return null; } };
const gravarLS = (k, v) => { try { if (v == null) localStorage.removeItem(k); else localStorage.setItem(k, JSON.stringify(v)); } catch { /* sem storage */ } };

// A cotação aberta (redesenho de 30/10, análise de UX): preço colado no produto e sempre à vista, cabeçalho e rodapé
// fixos com o progresso e o Enviar, validação ao sair do campo, Enter desce, rascunho guardado no navegador.
export default function CotacaoDetalhe() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  // ?editar=1 só no dev: deixa o admin no "Ver como" ver os campos como o fornecedor vê (o servidor continua recusando o envio)
  const previaDev = process.env.NODE_ENV === 'development' && new URLSearchParams(window.location.search).has('editar');
  const soLeitura = !!user?.visualizando_como && !previaDev;
  const chaveRasc = `cotacao-rascunho-${user?.usuario_id || user?.id || 'x'}-${id}`;
  const [p, setP] = useState(null);
  const [erro, setErro] = useState('');
  const [txt, setTxt] = useState({});          // { codpro: '12,34' } o que está digitado
  const [tocados, setTocados] = useState({});  // campos que já perderam o foco (só eles mostram erro)
  const [enviando, setEnviando] = useState(false);
  const [filtro, setFiltro] = useState('todos');
  const [busca, setBusca] = useState('');
  const [porLoja, setPorLoja] = useState(false);
  const [rascunho, setRascunho] = useState(null); // preços digitados e não enviados, achados ao abrir
  const [histAberto, setHistAberto] = useState(false);
  const inputs = useRef({});
  const topo = useRef(null);
  const pagina = useRef(null);

  const doServidor = (d) => Object.fromEntries(d.itens.map((i) => [i.codpro, precoTxt(i.preco)]));
  const ler = useCallback(() => api.get(`/cotacoes/${id}`).then((r) => {
    setP(r.data); setTxt(doServidor(r.data)); setTocados({});
    const rs = lerLS(chaveRasc);
    const difere = rs && r.data.itens.some((i) => rs[i.codpro] !== undefined && (paraNumero(rs[i.codpro]) ?? null) !== (i.preco ?? null));
    setRascunho(difere ? rs : null);
    if (!difere) gravarLS(chaveRasc, null);
  }).catch((e) => setErro(msgErro(e, 'Erro ao abrir a cotação.'))), [id, chaveRasc]);
  useEffect(() => { ler(); }, [ler]);

  const podeEditar = !!(p && p.aberta && !soLeitura);
  const mudou = p ? p.itens.some((i) => (paraNumero(txt[i.codpro]) ?? null) !== (i.preco ?? null)) : false;
  const invalidos = useMemo(() => (p ? p.itens.filter((i) => erroPreco(txt[i.codpro])).map((i) => i.codpro) : []), [p, txt]);
  const preenchidos = p ? p.itens.filter((i) => paraNumero(txt[i.codpro]) > 0).length : 0;
  const totalCotado = p ? p.itens.reduce((s, i) => { const n = paraNumero(txt[i.codpro]); return s + (n > 0 ? n * i.total : 0); }, 0) : 0;
  const ultimoEnvio = p?.envios.find((h) => h.evento === 'resposta');
  const pedidoDeNovo = p?.envios.find((h) => h.evento === 'pedido_de_novo' && (!ultimoEnvio || h.id > ultimoEnvio.id) && p.respostas > 0);

  // rascunho: guarda o que foi digitado e não enviado (sair sem querer não perde nada)
  useEffect(() => {
    if (!podeEditar || rascunho) return undefined;
    const t = setTimeout(() => gravarLS(chaveRasc, mudou ? txt : null), 400);
    return () => clearTimeout(t);
  }, [txt, mudou, podeEditar, rascunho, chaveRasc]);
  // fechar a aba com preço não enviado pede confirmação do navegador
  useEffect(() => {
    if (!podeEditar || !mudou) return undefined;
    const f = (e) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', f);
    return () => window.removeEventListener('beforeunload', f);
  }, [podeEditar, mudou]);

  // altura do topo fixo → o cabeçalho da tabela gruda logo abaixo dele
  useLayoutEffect(() => {
    if (!topo.current || !pagina.current) return undefined;
    const ro = new ResizeObserver(() => {
      if (!topo.current) return;
      const t = parseFloat(getComputedStyle(topo.current).top) || 0;   // 36px quando tem a faixa do "Ver como"
      pagina.current?.style.setProperty('--ct-topo-h', `${t + topo.current.offsetHeight}px`);
    });
    ro.observe(topo.current);
    return () => ro.disconnect();
  }, [p]);

  const q = busca.trim().toLowerCase();
  const visiveis = useMemo(() => (p ? p.itens.filter((i) => {
    const tem = paraNumero(txt[i.codpro]) > 0;
    if (filtro === 'sem' && tem) return false;
    if (filtro === 'com' && !tem) return false;
    return !q || [i.descr, i.codpro, i.codprofabricante].some((x) => String(x || '').toLowerCase().includes(q));
  }) : []), [p, txt, filtro, q]);

  // foco: rola pro meio da tela e seleciona o conteúdo
  const focar = (codpro) => { const el = inputs.current[codpro]; if (el) { el.focus(); el.select(); el.scrollIntoView({ block: 'center', behavior: 'smooth' }); } };
  // ao abrir, o cursor já vai pro 1º preço vazio
  const focouAoAbrir = useRef(false);
  useEffect(() => {
    if (!podeEditar || focouAoAbrir.current) return;
    focouAoAbrir.current = true;
    const vazio = p.itens.find((i) => !txt[i.codpro]);
    if (vazio) setTimeout(() => { const el = inputs.current[vazio.codpro]; if (el) { el.focus({ preventScroll: true }); } }, 50);
  }, [podeEditar, p, txt]);
  const proximoSemPreco = () => {
    const ordem = visiveis.length ? visiveis : p.itens;
    const atual = ordem.findIndex((i) => inputs.current[i.codpro] === document.activeElement);
    const alvo = [...ordem.slice(atual + 1), ...ordem.slice(0, atual + 1)].find((i) => !(paraNumero(txt[i.codpro]) > 0));
    if (alvo) focar(alvo.codpro); else toastOk('Todos os produtos visíveis já têm preço.');
  };
  const teclas = (e, k) => {
    const ordem = visiveis;
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) { e.preventDefault(); enviar(); return; }
    if (e.key === 'Enter' || e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      const alvo = ordem[k + (e.key === 'ArrowUp' ? -1 : 1)];
      if (alvo) focar(alvo.codpro);
      else if (e.key === 'Enter') document.getElementById('ct-enviar')?.focus();
    }
  };

  const voltar = async () => {
    if (podeEditar && mudou && !(await confirmar({ titulo: 'Sair sem enviar?', texto: 'Os preços que você digitou ficam guardados neste navegador, mas o Centrão só recebe quando você enviar.', confirmarTexto: 'Sair' }))) return;
    navigate('/cotacoes');
  };

  const enviar = async () => {
    if (!podeEditar || enviando) return;
    if (invalidos.length) {
      setTocados(Object.fromEntries(p.itens.map((i) => [i.codpro, true])));
      setFiltro('todos'); setBusca('');
      const prim = p.itens.find((i) => invalidos.includes(i.codpro));
      toastErro(`Corrija o preço de ${invalidos.length === 1 ? '1 produto' : `${invalidos.length} produtos`} (marcado${invalidos.length === 1 ? '' : 's'} em vermelho).`);
      setTimeout(() => focar(prim.codpro), 60);
      return;
    }
    if (!preenchidos) { toastErro('Preencha o preço de pelo menos um produto.'); return; }
    const faltam = p.itens.length - preenchidos;
    if (faltam > 0 && !(await confirmar({ titulo: 'Enviar a cotação?', texto: `${faltam} produto${faltam === 1 ? '' : 's'} sem preço ${faltam === 1 ? 'vai' : 'vão'} como "não cotado". Você pode completar e reenviar enquanto a cotação estiver aberta.`, confirmarTexto: 'Enviar' }))) return;
    setEnviando(true);
    try {
      const r = await api.post(`/cotacoes/${id}/responder`, { precos: p.itens.map((i) => ({ codpro: i.codpro, preco: paraNumero(txt[i.codpro]) })) });
      gravarLS(chaveRasc, null);
      toastOk(`Cotação enviada: ${r.data.com_preco} de ${r.data.de} produtos com preço.`);
      await ler();
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (e) { toastErro(msgErro(e, 'Não foi possível enviar a cotação.')); } finally { setEnviando(false); }
  };

  if (erro) return <div><button type="button" className="btn-link mb-3" onClick={() => navigate('/cotacoes')}><i className="bi bi-arrow-left me-1" />Cotações</button><div className="alert alert-danger">{erro}</div></div>;
  if (!p) return <div className="text-center text-muted py-5"><div className="spinner-border spinner-border-sm me-2" />Carregando a cotação...</div>;

  const temEmbalagem = p.itens.some((i) => i.unid_fornecedor);
  // loja pelo nome da cidade, nunca o número (regra do portal — backend/helpers/lojas.js)
  const nomeL = (l) => p.nomesLojas?.[l] || 'Loja';
  const pct = Math.round((preenchidos / p.itens.length) * 100);
  const contagem = { todos: p.itens.length, sem: p.itens.length - preenchidos, com: preenchidos };
  const textoBotao = enviando ? 'Enviando...' : !mudou && p.respostas > 0 ? 'Nada mudou desde o último envio' : p.respostas > 0 ? 'Reenviar cotação' : 'Enviar cotação';

  return (
    <div className="ct-pagina" ref={pagina}>
      <div className="ct-topo">
        <div className="ct-topo-linha">
          <button type="button" className="ct-voltar" onClick={voltar}><i className="bi bi-arrow-left" /><span>Cotações</span></button>
          <h4 className="ct-titulo">Cotação nº {p.cotacao_id}</h4>
          <Situacao c={{ ...p, com_preco: p.respostas > 0 ? p.itens.filter((i) => i.preco > 0).length : null, itens: p.itens.length }} />
        </div>
        <div className="ct-sub">Pedida em {fmtDataHora(p.pedido_em)} por <b>{nomeBonito(p.pedido_nome) || 'Compras'}</b> · {p.fornecedor_nome} · {p.itens.length} produtos</div>

        {soLeitura && <div className="ct-aviso neutro"><i className="bi bi-eye me-1" />Modo "Ver como": só visualização, os campos de preço ficam travados.</div>}
        {!p.aberta && <div className="ct-aviso neutro"><i className="bi bi-lock me-1" />O Centrão encerrou esta cotação. Os preços ficam só para consulta.</div>}
        {p.aberta && pedidoDeNovo && <div className="ct-aviso laranja"><i className="bi bi-arrow-repeat me-1" />O Centrão atualizou os produtos ou as quantidades em {fmtDataHora(pedidoDeNovo.criado_em)}. Confira e reenvie.</div>}
        {p.aberta && !pedidoDeNovo && p.respostas > 0 && <div className="ct-aviso"><i className="bi bi-check-circle me-1" />Enviada em {fmtDataHora(p.respondido_em)} por {p.respondido_nome || 'você'}{p.respostas > 1 ? ` (${p.respostas}º envio)` : ''}. Você pode alterar e reenviar enquanto a cotação estiver aberta.</div>}
        {podeEditar && rascunho && (
          <div className="ct-aviso laranja ct-rascunho">
            <span><i className="bi bi-journal-text me-1" />Você digitou preços nesta cotação e não enviou.</span>
            <span>
              <button type="button" className="btn-primary-for ct-btn-sm" onClick={() => { setTxt((t) => ({ ...t, ...rascunho })); setRascunho(null); }}>Recuperar</button>
              <button type="button" className="btn-link ms-3" onClick={() => { gravarLS(chaveRasc, null); setRascunho(null); }}>Descartar</button>
            </span>
          </div>
        )}
        {podeEditar && <div className="ct-regra"><i className="bi bi-info-circle me-1" />Informe o <b>preço unitário na unidade do produto</b> (a da coluna Qtd total). Deixe em branco o que não for cotar.</div>}
      </div>

      {/* só a busca/filtros fica presa no topo (o título rola; o rodapé tem o progresso e o Enviar) */}
      <div className="ct-fixo" ref={topo}>
        <div className="ct-ferramentas">
          <div className="pd-busca"><i className="bi bi-search" /><input value={busca} onChange={(e) => setBusca(e.target.value)} placeholder="Buscar produto ou código..." /></div>
          <div className="pd-seg">
            {[['todos', 'Todos'], ['sem', 'Sem preço'], ['com', 'Com preço']].map(([k, r]) => (
              <button key={k} type="button" className={filtro === k ? 'ativo' : ''} onClick={() => setFiltro(k)}>{r} <span className="ct-seg-n">{contagem[k]}</span></button>
            ))}
          </div>
          {p.lojas.length > 1 && (
            <button type="button" className={`ct-toggle ${porLoja ? 'ativo' : ''}`} onClick={() => setPorLoja((v) => !v)} title="Mostra uma coluna por loja">
              <i className={`bi ${porLoja ? 'bi-toggle-on' : 'bi-toggle-off'} me-1`} />Ver por loja
            </button>
          )}
        </div>
      </div>

      <div className="ct-tab-wrap">
        <table className={`table table-for ct-tab ${porLoja ? 'por-loja' : ''}`}>
          <thead><tr>
            <th className="nao-ord ct-c-fab">Cód. fabricante</th>
            <th className="nao-ord">Produto</th>
            {porLoja ? p.lojas.map((l) => <th key={l} className="nao-ord num ct-c-loja">{nomeL(l)}</th>) : (p.lojas.length > 1 && <th className="nao-ord ct-c-lojas">Lojas</th>)}
            <th className="nao-ord num ct-c-qtd">Qtd total</th>
            <th className="nao-ord num ct-c-preco">Preço unitário</th>
            <th className="nao-ord num ct-c-total">Total</th>
          </tr></thead>
          <tbody>
            {visiveis.length === 0 && <tr><td colSpan={6 + (porLoja ? p.lojas.length : 0)} className="text-center text-muted py-4">Nenhum produto {filtro === 'sem' ? 'sem preço' : filtro === 'com' ? 'com preço' : ''}{q ? ` com "${busca}"` : ''}.</td></tr>}
            {visiveis.map((i, k) => {
              const n = paraNumero(txt[i.codpro]);
              const msg = tocados[i.codpro] ? erroPreco(txt[i.codpro]) : null;
              const aviso = !msg && tocados[i.codpro] && desconfia(n, i.preco);
              const u = un(i.unid);
              const lojas = p.lojas.filter((l) => i.lojas[l]);
              return (
                <tr key={i.codpro} className={`${n > 0 ? 'com-preco' : ''} ${msg ? 'com-erro' : ''}`}>
                  <td className="ct-c-fab" data-label="Cód. fabricante">{i.codprofabricante || '—'}</td>
                  <td className="ct-c-prod">
                    <div className="ct-descr">{i.descr}</div>
                    <small>{i.codprofabricante && <span className="ct-so-cel">Fab. {i.codprofabricante} · </span>}Cód. Centrão {i.codpro}{temEmbalagem && i.unid_fornecedor ? ` · Sua embalagem: ${i.unid_fornecedor}` : ''}</small>
                  </td>
                  {porLoja
                    ? p.lojas.map((l) => <td key={l} className="num ct-c-loja" data-label={nomeL(l)}>{i.lojas[l] ? fmtQtd(i.lojas[l]) : '—'}</td>)
                    : (p.lojas.length > 1 && <td className="ct-c-lojas" data-label="Lojas">{lojas.map((l) => <span key={l} className="ct-loja">{nomeL(l)}: <b>{fmtQtd(i.lojas[l])}</b></span>)}</td>)}
                  <td className="num ct-c-qtd" data-label="Qtd"><b>{fmtQtd(i.total)}</b> <span className="ct-un">{u}</span></td>
                  <td className="ct-c-preco" data-label="Preço unitário">
                    {podeEditar ? (
                      <>
                        <div className={`ct-preco-grupo ${msg ? 'erro' : ''}`}>
                          <span>R$</span>
                          <input ref={(el) => { inputs.current[i.codpro] = el; }} type="text" inputMode="numeric" enterKeyHint="next" autoComplete="off"
                            aria-label={`Preço unitário de ${i.descr}`} aria-invalid={!!msg}
                            value={txt[i.codpro] ?? ''} placeholder="0,00"
                            onChange={(e) => setTxt((t) => ({ ...t, [i.codpro]: digitarBanco(e.target.value) }))}
                            onPaste={(e) => {   // colar "12,50" / "12.5" / "R$ 1.250,00" lê o número inteiro, não só os dígitos
                              const v = paraNumero(e.clipboardData.getData('text'));
                              if (v > 0) { e.preventDefault(); setTxt((t) => ({ ...t, [i.codpro]: precoTxt(Math.round(v * 100) / 100) })); }
                            }}
                            onFocus={(e) => e.target.select()}
                            onBlur={() => setTocados((t) => ({ ...t, [i.codpro]: true }))}
                            onKeyDown={(e) => teclas(e, k)} />
                          <span>/{u}</span>
                        </div>
                        {msg && <div className="ct-msg erro">{msg}</div>}
                        {aviso && <div className="ct-msg aviso">Confira: no último envio era {moeda(i.preco)}</div>}
                      </>
                    ) : <span className="ct-preco-txt">{i.preco ? <>{moeda(i.preco)}<small>/{u}</small></> : <span className="text-muted">não cotado</span>}</span>}
                  </td>
                  <td className="num ct-c-total" data-label="Total">{n > 0 ? moeda(n * i.total) : <span className="text-muted">—</span>}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="ct-rodape">
        <div className="ct-progresso">
          <span className="ct-barra"><span style={{ width: `${pct}%` }} /></span>
          <span><b>{preenchidos} de {p.itens.length}</b> com preço{totalCotado > 0 && <span className="ct-total-cotado"> · Total {moeda(totalCotado)}</span>}</span>
        </div>
        {podeEditar && preenchidos < p.itens.length && <button type="button" className="btn-link ct-proximo" onClick={proximoSemPreco}><i className="bi bi-arrow-down-circle me-1" />Próximo sem preço</button>}
        {podeEditar
          ? <button id="ct-enviar" type="button" className="btn-primary-for ct-enviar" disabled={enviando || !mudou} onClick={enviar} title="Ctrl+Enter também envia">
              {enviando ? <span className="spinner-border spinner-border-sm me-1" /> : (mudou || !p.respostas) && <i className="bi bi-send me-1" />}{textoBotao}
            </button>
          : <button type="button" className="btn-outline-for" onClick={() => navigate('/cotacoes')}>Voltar</button>}
      </div>

      <div className="ct-hist-card">
        <button type="button" className="ct-hist-titulo" onClick={() => setHistAberto((v) => !v)}>
          <i className={`bi ${histAberto || p.envios.length <= 3 ? 'bi-chevron-down' : 'bi-chevron-right'} me-1`} /><i className="bi bi-clock-history me-1" />Histórico ({p.envios.length})
        </button>
        {(histAberto || p.envios.length <= 3) && p.envios.map((h) => (
          <div key={h.id} className="ct-hist">
            <b>{fmtDataHora(h.criado_em)}</b> · {h.evento === 'resposta'
              ? <>enviada por {h.quem_nome || 'você'}: {h.detalhe?.com_preco ?? '?'} de {h.detalhe?.de ?? '?'} com preço</>
              : <>{h.evento === 'pedido' ? 'pedida' : 'pedida de novo (produtos ou quantidades atualizados)'} por {nomeBonito(h.quem_nome) || 'Compras'}</>}
            {h.evento === 'resposta' && h.detalhe?.mudou?.length > 0 && (
              <div className="ct-hist-mudou">
                {h.detalhe.mudou.map((m) => {
                  const it = p.itens.find((x) => x.codpro === m.codpro);
                  return <div key={m.codpro}>{it?.descr || m.codpro}: {m.de ? moeda(m.de) : 'sem preço'} → <b>{m.para ? moeda(m.para) : 'sem preço'}</b></div>;
                })}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
